import { useState, useEffect } from 'react';
import {
  Files, Edit, Eye, ChevronDown, Save, Search, Phone,
  Type, FileEdit, RefreshCw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { API, axios, showToast } from './adminHelpers';

const PAGE_LIST = [
  { id: 'home', name: 'Home', path: '/', description: 'Main landing page with booking form' },
  { id: 'fleet', name: 'Our Fleet', path: '/fleet', description: 'Vehicle types and options' },
  { id: 'airport', name: 'Airport Transfers', path: '/airport-transfers', description: 'Airport services' },
  { id: 'corporate', name: 'Corporate', path: '/corporate', description: 'Business & corporate services' },
  { id: 'events', name: 'Events', path: '/events', description: 'Event transportation services' },
  { id: 'contact', name: 'Contact', path: '/contact', description: 'Contact form and information' },
];

const DEFAULT_CONTENT = {
  home: { hero_title: 'Melbourne Taxi Booking', hero_subtitle: 'Fast, reliable taxi service across Melbourne and Victoria', cta_text: 'Book Now' },
  fleet: { hero_title: 'Our Fleet', hero_subtitle: 'Choose from our range of quality vehicles', intro_text: 'We offer a variety of vehicles to suit your needs.' },
  airport: { hero_title: 'Airport Transfers', hero_subtitle: 'Reliable airport pickup and drop-off services', intro_text: 'Travel to and from Melbourne Airport in comfort.' },
  corporate: { hero_title: 'Corporate Services', hero_subtitle: 'Professional transport for your business', intro_text: 'Tailored solutions for corporate clients.' },
  events: { hero_title: 'Event Transport', hero_subtitle: 'Getting you to Melbourne events on time', intro_text: 'Perfect for concerts, sports, and special occasions.' },
  contact: { hero_title: 'Contact Us', hero_subtitle: 'Get in touch with our team', phone: '1300 096 685', email: '13cabsbook.com.au@gmail.com' },
};

export const PagesManager = ({ authHeader }) => {
  const [editingPage, setEditingPage] = useState(null);
  const [pageContent, setPageContent] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPageContent = async (pageId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pages/${pageId}`, { headers: authHeader });
      setPageContent(res.data.sections && Object.keys(res.data.sections).length > 0 ? res.data.sections : DEFAULT_CONTENT[pageId] || {});
    } catch {
      setPageContent(DEFAULT_CONTENT[pageId] || {});
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    fetchPageContent(page.id);
  };

  const handleSave = async () => {
    if (!editingPage) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${editingPage.id}`, {
        page_id: editingPage.id,
        sections: pageContent
      }, { headers: authHeader });
      showToast('Page content saved!');
    } catch {
      showToast('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateContent = (key, value) => {
    setPageContent(prev => ({ ...prev, [key]: value }));
  };

  if (editingPage) {
    return (
      <div className="space-y-6" data-testid="page-editor">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => setEditingPage(null)} className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-lg" data-testid="page-editor-back">
              <ChevronDown className="w-5 h-5 rotate-90" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Edit: {editingPage.name}</h1>
              <p className="text-sm text-gray-500">{editingPage.path}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <a href={`https://13cabsbook.com.au${editingPage.path}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">
              <Eye className="w-4 h-4" /> Preview
            </a>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary-hover" data-testid="save-page-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="font-semibold text-lg mb-4 flex items-center gap-2"><Type className="w-5 h-5" /> Hero Section</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input value={pageContent.hero_title || ''} onChange={(e) => updateContent('hero_title', e.target.value)} placeholder="Page title" className="mt-1" data-testid="page-hero-title" />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Textarea value={pageContent.hero_subtitle || ''} onChange={(e) => updateContent('hero_subtitle', e.target.value)} placeholder="Page subtitle or description" rows={2} className="mt-1" data-testid="page-hero-subtitle" />
                  </div>
                  {editingPage.id === 'home' && (
                    <div>
                      <Label>CTA Button Text</Label>
                      <Input value={pageContent.cta_text || ''} onChange={(e) => updateContent('cta_text', e.target.value)} placeholder="Book Now" className="mt-1" data-testid="page-cta-text" />
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="font-semibold text-lg mb-4 flex items-center gap-2"><FileEdit className="w-5 h-5" /> Main Content</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Introduction Text</Label>
                    <Textarea value={pageContent.intro_text || ''} onChange={(e) => updateContent('intro_text', e.target.value)} placeholder="Introduction paragraph for this page..." rows={4} className="mt-1" data-testid="page-intro-text" />
                  </div>
                  <div>
                    <Label>Additional Content (HTML supported)</Label>
                    <Textarea value={pageContent.body_content || ''} onChange={(e) => updateContent('body_content', e.target.value)} placeholder="Additional page content..." rows={8} className="mt-1 font-mono text-sm" data-testid="page-body-content" />
                  </div>
                </div>
              </div>

              {editingPage.id === 'contact' && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="font-semibold text-lg mb-4 flex items-center gap-2"><Phone className="w-5 h-5" /> Contact Information</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Phone Number</Label>
                      <Input value={pageContent.phone || ''} onChange={(e) => updateContent('phone', e.target.value)} placeholder="1300 096 685" className="mt-1" data-testid="page-contact-phone" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input value={pageContent.email || ''} onChange={(e) => updateContent('email', e.target.value)} placeholder="email@example.com" className="mt-1" data-testid="page-contact-email" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="font-semibold text-lg mb-4 flex items-center gap-2"><Search className="w-5 h-5" /> Page SEO</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Meta Title</Label>
                    <Input value={pageContent.meta_title || ''} onChange={(e) => updateContent('meta_title', e.target.value)} placeholder="SEO title for this page" className="mt-1" data-testid="page-meta-title" />
                    <p className="text-xs text-gray-400 mt-1">{(pageContent.meta_title || '').length}/60 characters</p>
                  </div>
                  <div>
                    <Label>Meta Description</Label>
                    <Textarea value={pageContent.meta_description || ''} onChange={(e) => updateContent('meta_description', e.target.value)} placeholder="SEO description for this page" rows={3} className="mt-1" data-testid="page-meta-desc" />
                    <p className="text-xs text-gray-400 mt-1">{(pageContent.meta_description || '').length}/160 characters</p>
                  </div>
                  <div>
                    <Label>Keywords</Label>
                    <Input value={pageContent.keywords || ''} onChange={(e) => updateContent('keywords', e.target.value)} placeholder="taxi, melbourne, airport" className="mt-1" data-testid="page-keywords" />
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-800"><strong>Tip:</strong> Changes here will be reflected on the live site after saving. SEO changes may take time to appear in search results.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pages-manager">
      <h1 className="text-2xl font-bold text-gray-900">Pages</h1>
      <p className="text-gray-600">Manage your website pages. Click edit to modify content and SEO settings.</p>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="divide-y divide-gray-200">
          {PAGE_LIST.map((page) => (
            <div key={page.id} className="p-4 flex items-center justify-between hover:bg-gray-50" data-testid={`page-item-${page.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Files className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{page.name}</div>
                    <div className="text-sm text-gray-500">{page.path}</div>
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-400 hidden md:block mr-4">{page.description}</div>
              <div className="flex items-center gap-2">
                <a href={`https://13cabsbook.com.au${page.path}`} target="_blank" rel="noreferrer" className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="View page">
                  <Eye className="w-4 h-4" />
                </a>
                <button onClick={() => handleEdit(page)} className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg" title="Edit page" data-testid={`edit-page-${page.id}`}>
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
